import type { AxiosInstance, AxiosRequestConfig } from 'axios';

interface AuthClient {
    getTokenSilently: (options?: Record<string, unknown>) => Promise<string>;
}

let authClient: AuthClient | null = null;
let overrideToken: string | null = null;
let anonymousMode = false;

let resolveAuthReady: (() => void) | null = null;
let authReady: Promise<void> = new Promise(resolve => {
    resolveAuthReady = resolve;
});

export function setAuthClient(client: AuthClient | null): void {
    authClient = client;
}

// Auth0's initial session check runs async after the app mounts. Until it resolves,
// getTokenSilently() can spuriously fail ("Login required"), which used to make the
// interceptor silently send the request unauthenticated. Callers of
// attachAccessTokenInterceptor now wait on this before attaching a token so requests
// fired during that startup window don't race ahead of the token.
export function markAuthReady(): void {
    if (resolveAuthReady) {
        resolveAuthReady();
        resolveAuthReady = null;
    }
}

// Concurrent requests would otherwise each call getTokenSilently() independently,
// firing redundant (and, with iframe-based renewal, flaky) refreshes in parallel.
// Share one in-flight fetch so bursts of requests wait on the same token.
let inFlightToken: Promise<string> | null = null;

function getSilentToken(client: AuthClient): Promise<string> {
    if (!inFlightToken) {
        inFlightToken = client.getTokenSilently().finally(() => {
            inFlightToken = null;
        });
    }
    return inFlightToken;
}

// Anonymous routes (family/contributor links) have no Auth0 session by design. While active,
// Auth0 is never consulted: callers get the backend-issued override token if there is one,
// otherwise requests go out unauthenticated. Driven by the `anonymous` route meta flag.
export function setAnonymousMode(enabled: boolean): void {
    anonymousMode = enabled;
}

export async function getAccessToken(options?: Record<string, unknown>): Promise<string> {
    if (anonymousMode) {
        return overrideToken ?? '';
    }

    if (!authClient) {
        throw new Error('Auth client unavailable');
    }

    return authClient.getTokenSilently(options);
}

// Lets anonymous/contributor flows (backend-issued tribute & story tokens, not Auth0)
// swap what the interceptor attaches to $api calls without prop-drilling a raw token.
export function setApiOverrideToken(token: string): void {
    overrideToken = token;
}

export function clearApiOverrideToken(): void {
    overrideToken = null;
}

export function attachAccessTokenInterceptor(api: AxiosInstance): void {
    api.interceptors.request.use(async (config: AxiosRequestConfig) => {
        if (overrideToken) {
            config.headers = config.headers ?? {};
            (config.headers as Record<string, string>).Authorization = `Bearer ${overrideToken}`;
            return config;
        }

        if (config.skipAuth || anonymousMode) {
            return config;
        }

        await authReady;

        if (!authClient) {
            throw new Error('Auth client unavailable');
        }

        const token = await getSilentToken(authClient);
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;

        return config;
    });
}
